/* ExportedVideos.js */
/* @author: anghello */

import React, { Fragment, useEffect, useCallback, useState } from 'react';
import { Row, Col, Alert, Button, Card, CardBody, CardTitle } from 'reactstrap';
import { PageAnimation } from '../../Layout/Components/Animation';
import PageTitle from '../../Layout/Components/PageTitle';
import { useAuth } from '../../Context/Auth/AuthContext';
import Strings from '../../translations/Translations';
import PSO from '../../services/PSO';
import { toast } from 'react-toastify';
import { getRemixCover } from '../../helpers/GetRemixCover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlay } from '@fortawesome/free-solid-svg-icons';
import ModalRemoveExportedVideo from '../../Layout/Components/ModalRemoveExportedVideo';
import ModalShare from '../../Layout/Components/ModalShare';

const REMIX_URL = process.env.REACT_APP_PZ_REMIX_URL;
const MOVIE_MP4_BASE_URL = process.env.REACT_APP_MOVIE_MP4;
const maxElementsPerRequest = 24;

export default function ExportedVideos() {
  const { clearActiveCampaign, authenticated, toggleLoading } = useAuth();

  const [modalShareOpen, setModalShareOpen] = useState(false);
  const [selectedRemix, setSelectedRemix] = useState();
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [remixes, setRemixes] = useState();

  useEffect(() => { clearActiveCampaign(); }, [clearActiveCampaign]);

  const getMoreExportedVideos = useCallback(async (lastId) => {
    toggleLoading(true);
    const { un, al } = authenticated;
    try {
      const response = await PSO.getExportedListByUser({ un, al, rnmax: maxElementsPerRequest, lastId });
      if (response && response.its) {
        setRemixes([...remixes, ...response.its]);
        const isToShowLoadMore = (response.its.length === maxElementsPerRequest);
        setShowLoadMore(isToShowLoadMore);
      }
    } catch (err) {
      console.error(err);
      toast.error(Strings('error_while_getting_exported_videos'));
    }
    toggleLoading(false);
  }, [authenticated, setRemixes, toggleLoading, remixes]);

  const getExportedVideos = useCallback(async () => {
    toggleLoading(true);
    const { un, al } = authenticated;
    try {
      const response = await PSO.getExportedListByUser({ un, al, rnmax: maxElementsPerRequest });
      if (response && response.its) {
        setRemixes(response.its);
        const isToShowLoadMore = (response.its.length === maxElementsPerRequest);
        setShowLoadMore(isToShowLoadMore);
      }
    } catch (err) {
      console.error(err);
      toast.error(Strings('error_while_getting_exported_videos'));
    }
    toggleLoading(false);
  }, [authenticated, setRemixes, toggleLoading]);
  useEffect(() => { getExportedVideos(); }, [getExportedVideos]);

  const [modalRemove, setModalRemove] = useState(false);
  const [removeMovid, setRemoveMovid] = useState();
  const removeExportedVideo = useCallback(async () => {
    toggleLoading(true);
    const { un, al } = authenticated;
    const mid = removeMovid;
    try {
      const pars = { un, al, mid };
      const response = await PSO.removeExported(pars);
      if (response && response.result === 'success') {
        setRemoveMovid();
        getExportedVideos();
      } else {
        toast.error(Strings('error_while_removing_exported_video'));
      }
    } catch (err) {
      console.error(err);
      toast.error(Strings('error_while_removing_exported_video'));
    }
    toggleLoading(false);
  }, [authenticated, removeMovid, setRemoveMovid, getExportedVideos, toggleLoading]);

  const selectRemixToShare = useCallback(async (rid) => {
    if (rid) {
      const { un, al } = authenticated;

      toggleLoading(true);
      try {
        const response = await PSO.getRemixPars({ un, al, rid });
        if (response && response.its && response.its[0]) {
          setSelectedRemix(response.its[0]);
          setModalShareOpen(true);
        }
      } catch (err) {
        console.error(err);
      }
      toggleLoading(false);
    }
  }, [toggleLoading, authenticated, setSelectedRemix, setModalShareOpen]);

  return (
    <Fragment>
      <PageAnimation>
        <PageTitle
          heading={Strings('my_exported_videos')}
          icon='pe-7s-user'
        />

        <Row className='mt-5'>
          {remixes && remixes.length === 0 && (
            <Col sm={12} md={6}>
              <Alert color='secondary'>
                {Strings('no_exported_videos')}
              </Alert>
            </Col>
          )}
          {remixes && remixes.length > 0 && remixes.map((item) => (
            <Col xs={12} sm={12} md={4} lg={3} key={`${item.rid}-${item.movid}`} className='mb-3'>
              <Card className='shadow'>
                <img
                  src={getRemixCover(item)}
                  title={item.ti}
                  alt={item.bo}
                  className='pointer'
                  onClick={() => window.open(`${REMIX_URL}?r=${item.rid}`)}
                />
                <CardBody>
                  <CardTitle tag="h5">
                    {item.ti}
                  </CardTitle>
                  <div className='d-flex justify-content-between'>
                    <div>
                      <a
                        href={`${MOVIE_MP4_BASE_URL.replace('{id}', item.movid)}`}
                        className='btn btn-primary pb-btn-1 mr-1'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {Strings('download')}
                      </a>
                      <button
                        className='btn btn-secondary'
                        onClick={() => {
                          setRemoveMovid(item.movid);
                          setModalRemove(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                    <div>
                      <Button onClick={() => { selectRemixToShare(item.rid); }}>
                        <FontAwesomeIcon icon={faPlay} />
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}

          {showLoadMore && (
            <Col sm={12} className='d-flex justify-content-center mt-5'>
              <Button
                className='pb-btn-1'
                size='lg'
                onClick={() => {
                  const lastRemix = remixes[remixes.length - 1];
                  const lastId = lastRemix.movid;
                  if (lastRemix && lastRemix.rid) { getMoreExportedVideos(lastId); }
                }}
              >
                {Strings('load_more')}
              </Button>
            </Col>
          )}
        </Row>

        <ModalShare open={modalShareOpen} setOpen={setModalShareOpen} remixPars={selectedRemix} />
        <ModalRemoveExportedVideo open={modalRemove} setOpen={setModalRemove} confirm={removeExportedVideo} />

      </PageAnimation>
    </Fragment>
  );
}
